import React from 'react';
import { ArrowRight, ArrowLeft, Check, X, Mail, Send, Target, Zap, BarChart3, Bot, Calendar, Users, MessageSquare, TrendingUp, Building2, Briefcase, Clock, Brain, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const whyTraditionalFails = [
  'Blast generic messages to cold lists',
  'Lack real buyer intent',
  'Stop at "send" instead of managing conversations',
  'Rely on manual follow-ups'
];

const sendingCapabilities = [
  { icon: Shield, title: 'Scale Without Killing Deliverability', items: ['Intelligent email distribution to avoid spam filters', 'High deliverability infrastructure', 'Scalable sending without inbox burnout'] },
  { icon: Target, title: 'Personalization at Scale', items: ['Dynamic personalization fields', 'Custom subject lines and body copy per prospect', 'Messaging aligned to buyer intent and persona'] },
  { icon: Clock, title: 'Scheduling & Automation', items: ['Send emails at optimal times', 'Automate follow-ups based on opens, clicks, and replies', 'Remove manual tasks from your sales process'] },
  { icon: BarChart3, title: 'Real-Time Analytics', items: ['Open rates', 'Click-through rates', 'Reply and engagement tracking', 'Performance insights to continuously improve'] }
];

const aiAgentFeatures = [
  {
    icon: Target,
    title: 'AI-Driven Lead Generation',
    description: 'AI identifies and engages qualified prospects, focuses on high-intent, high-quality leads, and eliminates wasted outreach.',
    highlight: 'Your sales team talks only to the right people.'
  },
  {
    icon: MessageSquare,
    title: 'Personalized AI Interactions',
    description: 'Intelligent, human-like conversations with trust-building, context-aware responses and continuous engagement that nurtures interest.',
    highlight: 'Every interaction feels relevant and timely.'
  },
  {
    icon: Calendar,
    title: 'Automated Meeting Setup',
    description: 'AI qualifies leads in real time, books meetings autonomously, and syncs directly with your calendar.',
    highlight: 'From first email to booked meeting — hands-free.'
  }
];

const targetingCapabilities = [
  'Ideal Customer Profile (ICP) development',
  'Detailed customer personas',
  'Prospect validation & quality assurance',
  'Rich prospect enrichment with key business data',
  'Intent data capture to reach buyers at the perfect time',
  'Seamless CRM integration',
  'Continuous learning and optimization'
];

const copywritingFeatures = [
  'Bespoke, hyper-personalized email campaigns',
  'Autonomous email sending and replies',
  'Real-time message reading and response handling',
  'Scheduled follow-ups that never miss',
  'Action-oriented CTAs that drive replies and bookings',
  'Ongoing A/B testing with rotating templates'
];

const whoIsFor = [
  { icon: Building2, title: 'B2B companies' },
  { icon: Zap, title: 'SaaS & technology firms' },
  { icon: Briefcase, title: 'Agencies & consultants' },
  { icon: TrendingUp, title: 'High-ticket service providers' },
  { icon: Users, title: 'Sales teams that want scale' }
];

const results = [
  'Higher open and reply rates',
  'Lower cost per booked meeting',
  'Shorter sales cycles',
  'Fully automated outreach and follow-ups',
  'Predictable, scalable pipeline growth'
];

const AIEmailMarketing = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(139, 92, 246, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(16, 185, 129, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="max-w-xl">
                <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">AI Email Marketing</span>
                <h1 className="text-[clamp(2rem,4vw,3.5rem)] font-light leading-[1.1] text-black mb-6">
                  AI-Powered Email Marketing That Turns Intent Into{' '}
                  <span className="font-medium">Revenue</span>
                </h1>
                <p className="text-xl text-gray-700 mb-4">
                  Precision Email Outreach at Scale — Fully Automated by AI
                </p>
                <p className="text-lg text-gray-600 mb-8">
                  Send high-volume email campaigns with precision targeting, hyper-personalization, and AI agents that work your pipeline for you.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                  >
                    Book a Free AI Email Strategy Call <ArrowRight size={18} />
                  </a>
                  <a
                    href="#how-it-works"
                    className="inline-flex items-center gap-2 px-8 py-4 border border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-colors"
                  >
                    See How It Works
                  </a>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/1v2v830u_67ed36872e176b06f4d2a08f_hero-img.png" 
                    alt="AI Email Marketing Dashboard"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Traditional Email Fails */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  Why Traditional Email Marketing <span className="text-red-400">No Longer Works</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Most email campaigns fail because they:
                </p>
                <ul className="space-y-4 mb-8">
                  {whyTraditionalFails.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <X size={18} className="text-red-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-white font-medium mb-4">
                  That's why open rates drop, replies stall, and pipelines dry up.
                </p>
                <p className="text-blue-400 font-medium">
                  Mobyle changes that by combining intent data + AI automation + real-time engagement.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/r169jnpm_Screenshot%202025-12-19%20at%207.40.23%E2%80%AFAM.png" 
                    alt="Email Campaign Analytics"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* High-Volume Email Sending */}
        <section id="how-it-works" className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                High-Volume Email Sending With <span className="text-blue-600">Precision Targeting</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {sendingCapabilities.map((cap, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8">
                  <cap.icon className="w-10 h-10 text-blue-600 mb-4" />
                  <h3 className="text-xl font-medium text-black mb-4">{cap.title}</h3>
                  <ul className="space-y-2">
                    {cap.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-600">
                        <Check size={16} className="text-blue-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Agents Section */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
              <div>
                <h2 className="text-[clamp(2rem,5vw,4rem)] font-light text-black mb-6">
                  AI Agents That Automate Your{' '}
                  <span className="font-medium">Entire Sales Process</span>
                </h2>
                <p className="text-lg text-gray-600 mb-4">
                  Boost B2B Sales Conversions With Autonomous AI
                </p>
                <p className="text-gray-700">
                  Our AI agents don't just send emails — they manage conversations, qualify leads, and book meetings automatically.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/rqhshfvm_67e9a5d0e2ee3679ea5e8869_img-1.png" 
                    alt="AI Agent Automation"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {aiAgentFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
                  <feature.icon className="w-10 h-10 text-violet-600 mb-4" />
                  <h3 className="text-xl font-medium text-black mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <p className="text-violet-600 font-medium text-sm">{feature.highlight}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advanced AI Prospect Targeting */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/ogten8cx_67ec1ab59badc0a8893e22c2_img-2.png" 
                    alt="AI Prospect Targeting"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <span className="text-sm uppercase tracking-widest text-blue-600 mb-4 block">Advanced Targeting</span>
                <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                  Laser-Focused Targeting Built on Data
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Our system ensures every prospect is worth your time.
                </p>
                <ul className="space-y-3">
                  {targetingCapabilities.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check size={18} className="text-blue-500 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-black font-medium mt-6">
                  Your AI agents get smarter with every interaction.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Hyper-Personalized Outreach */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  Hyper-Personalized Outreach & <span className="text-blue-400">AI Copywriting</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  No templates. No generic copy.
                </p>
                <ul className="space-y-3 mb-8">
                  {copywritingFeatures.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check size={18} className="text-blue-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-white font-medium">
                  Your messaging constantly improves — automatically.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/1c4q1q2s_67ed356a477318fa8913fa8b_img-1.png" 
                    alt="AI Email Copywriting"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who Is This For */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Who AI-Powered Email Marketing Is For
              </h2>
              <p className="text-lg text-gray-600">
                This solution is ideal for:
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {whoIsFor.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <item.icon className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-black">{item.title}</h3>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-gray-700 font-medium">
              If outbound email matters to your revenue, this is built for you.
            </p>
          </div>
        </section>

        {/* Results */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Results You Can Expect
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((result, index) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-6 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Check size={20} className="text-blue-600" />
                    </div>
                    <p className="text-gray-800 font-medium text-left">{result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to Let AI Run Your{' '}
                <span className="font-medium">Email Marketing?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Stop manually sending emails. Start running an autonomous sales engine.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors mb-4"
              >
                Book a Free AI Email Strategy Call <ArrowRight size={18} />
              </a>
              <p className="text-gray-500 text-sm">
                No contracts. No guesswork. Just smarter outbound.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default AIEmailMarketing;
